import type { CSSProperties } from 'react';

import { useReducedMotion } from '../hooks/useReducedMotion.js';
import type { ComposerFlowPhase } from './ContextComposer.js';

type SignalStyle = CSSProperties & Record<`--${string}`, string | number>;

const PIXELS = [
  [13, 28, -54, -34, 7, '#EF00C8', 10, 0],
  [22, 63, -34, 24, 6, '#2E3CFF', 12, -3],
  [31, 18, -20, -18, 5, '#FF067F', 14, -7],
  [39, 76, -8, 31, 7, '#A30089', 11, -4],
  [48, 34, 5, -29, 6, '#2E3CFF', 13, -9],
  [57, 82, 18, 29, 4, '#EF00C8', 15, -1],
  [66, 22, 31, -23, 7, '#A30089', 12, -6],
] as const;

/** Signal pixels echo the supplied logo without becoming a static texture. */
export function HeroSignalField({
  phase,
  attention = false,
}: {
  readonly phase: ComposerFlowPhase;
  /** The composer holds something: the pixels lean toward it. */
  readonly attention?: boolean;
}): React.ReactElement | null {
  const reducedMotion = useReducedMotion();
  return (
    <span
      className="mj-signal-field"
      data-phase={phase}
      data-attention={attention ? 'true' : undefined}
      data-reduced-motion={reducedMotion ? 'true' : 'false'}
      aria-hidden="true"
    >
      {PIXELS.map(([x, y, gatherX, gatherY, size, color, duration, delay], index) => {
        const style: SignalStyle = {
          '--pixel-x': `${String(x)}%`,
          '--pixel-y': `${String(y)}%`,
          '--pixel-gather-x': `${String(gatherX)}px`,
          '--pixel-gather-y': `${String(gatherY)}px`,
          '--pixel-size': `${String(size)}px`,
          '--pixel-color': color,
          '--pixel-duration': `${String(duration)}s`,
          '--pixel-delay': `${String(delay)}s`,
        };
        return <span key={String(index)} className="mj-signal-pixel" style={style} />;
      })}
    </span>
  );
}
